import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Bell, Check, CheckCheck, X } from 'lucide-react';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const timeAgo = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

const typeLabel = (type) => {
  switch (type) {
    case 'ADMIN_MESSAGE': return { text: 'Admin', color: 'bg-purple-500/20 text-purple-400' };
    case 'BROADCAST': return { text: 'Broadcast', color: 'bg-blue-500/20 text-blue-400' };
    case 'SYSTEM': return { text: 'System', color: 'bg-amber-500/20 text-amber-400' };
    default: return { text: type, color: 'bg-zinc-500/20 text-zinc-400' };
  }
};

const NotificationBell = () => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // silent fail
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // silent fail
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // silent fail
    }
    setLoading(false);
  };

  const toggleOpen = () => {
    setIsOpen(prev => !prev);
    if (!isOpen) fetchNotifications();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleOpen}
        data-testid="notification-bell"
        className="relative p-2 rounded-xl border border-zinc-700 hover:border-brand-gold/50 hover:bg-brand-gold/5 transition-all duration-200"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
      >
        <Bell className={`w-4 h-4 text-brand-gold transition-transform ${unreadCount > 0 ? 'animate-[bellRing_2s_ease-in-out_infinite]' : ''}`} />
        {unreadCount > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1"
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-full mt-2 w-[380px] max-h-[480px] bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl shadow-black/50 z-50 overflow-hidden flex flex-col animate-[slideDown_0.15s_ease-out]"
          style={{ transformOrigin: 'top right' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-brand-text">Notifications</h3>
              {unreadCount > 0 && (
                <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded-full font-medium">
                  {unreadCount} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  disabled={loading}
                  data-testid="mark-all-read-btn"
                  className="text-xs text-brand-gold hover:text-brand-gold/80 transition-colors px-2 py-1 rounded-lg hover:bg-brand-gold/5 flex items-center gap-1"
                >
                  <CheckCheck className="w-3 h-3" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-lg hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1" data-testid="notification-list">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Bell className="w-8 h-8 text-zinc-600 mb-3" />
                <p className="text-sm text-zinc-500">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const label = typeLabel(notif.type);
                return (
                  <div
                    key={notif.id}
                    data-testid={`notification-item-${notif.id}`}
                    className={`px-4 py-3 border-b border-zinc-800/60 hover:bg-zinc-800/40 transition-colors cursor-default ${
                      !notif.read ? 'bg-brand-gold/[0.03]' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {!notif.read && (
                            <span className="w-2 h-2 rounded-full bg-brand-gold flex-shrink-0" />
                          )}
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${label.color}`}>
                            {label.text}
                          </span>
                          <span className="text-[10px] text-zinc-600">{timeAgo(notif.created_at)}</span>
                        </div>
                        {notif.subject && (
                          <p className="text-xs font-medium text-brand-text mb-0.5 truncate">{notif.subject}</p>
                        )}
                        <p className="text-xs text-zinc-400 line-clamp-2">{notif.message}</p>
                      </div>
                      {!notif.read && (
                        <button
                          onClick={(e) => { e.stopPropagation(); markAsRead(notif.id); }}
                          data-testid={`mark-read-${notif.id}`}
                          className="p-1 rounded-lg hover:bg-zinc-700 text-zinc-500 hover:text-brand-gold transition-colors flex-shrink-0"
                          title="Mark as read"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
